/**
 * Date Formatting Utilities
 * Formats dates in Indian format (dd/mm/yyyy)
 */

/**
 * Format date to dd/mm/yyyy (Indian format)
 * @param {string|Date} dateInput - Date string (YYYY-MM-DD) or Date object
 * @returns {string} Formatted date in dd/mm/yyyy format
 */
export const formatDateDDMMYYYY = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    let date;
    
    // Handle string input (YYYY-MM-DD format)
    if (typeof dateInput === 'string') {
      // If it's already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}/.test(dateInput)) {
        const [year, month, day] = dateInput.split('T')[0].split('-');
        return `${day}/${month}/${year}`;
      }
      // Otherwise, try to parse it
      date = new Date(dateInput);
    } else {
      date = dateInput;
    }
    
    if (!date || isNaN(date.getTime())) {
      return '';
    }
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format date to dd/mm/yyyy with time (Indian format)
 * @param {string|Date} dateInput - Date string or Date object
 * @param {string} timeInput - Time string (HH:MM:SS or HH:MM)
 * @returns {string} Formatted date and time
 */
export const formatDateTimeDDMMYYYY = (dateInput, timeInput) => {
  const dateStr = formatDateDDMMYYYY(dateInput);
  if (!dateStr) return '';
  
  if (timeInput) {
    // Format time to HH:MM (24-hour) or convert to 12-hour format
    const timeParts = timeInput.split(':');
    if (timeParts.length >= 2) {
      const hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${dateStr} ${String(hours12).padStart(2, '0')}:${minutes} ${ampm}`;
    }
    return dateStr;
  }
  
  return dateStr;
};

/**
 * Convert dd/mm/yyyy to YYYY-MM-DD for date input
 * @param {string} dateStr - Date string in dd/mm/yyyy format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const convertToYYYYMMDD = (dateStr) => {
  if (!dateStr) return '';
  
  try {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  } catch (error) {
    console.error('Error converting date:', error);
    return dateStr;
  }
};

/**
 * Format date for display (long format in Indian locale)
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date (e.g., "15 January 2024")
 */
export const formatDateLong = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    let date;
    if (typeof dateInput === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(dateInput)) {
        const [year, month, day] = dateInput.split('T')[0].split('-');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        date = new Date(dateInput);
      }
    } else {
      date = dateInput;
    }
    
    if (!date || isNaN(date.getTime())) {
      return '';
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Kolkata'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};
